import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { siteConfig, modsPortfolio, ellisRowSite } from "../data/mock";
import { ArrowRight, Check, ExternalLink, Send, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

// Smooth-scroll to hash targets when present
const HashScroller = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top, behavior: "smooth" });
        }
      });
    }
  }, [location]);
  return null;
};

export default function ModsPage() {
  const cfg = siteConfig.mods;
  return (
    <main data-testid="mods-page">
      <HashScroller />
      <Hero cfg={cfg} />
      <PortfolioSection />
      <FeaturedPropertySection />
      <ExternalListingsSection />
      <TimelineSection />
      <EllisRowSection />
      <InvestorsSection />
      <ContactSection cfg={cfg} />
    </main>
  );
}

/* ---------------- HERO ---------------- */
const Hero = ({ cfg }) => {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-end overflow-hidden bg-stone-950"
      data-testid="mods-hero"
    >
      <img
        src="/properties/1407-7th-ave-se/10.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/75 to-stone-950/30" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-32 pt-32 w-full">
        <img
          src="/logos/mods-symbol.png"
          alt=""
          className={`h-14 md:h-16 w-auto invert mb-8 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        />
        <span
          className={`font-body text-[11px] tracking-[0.32em] uppercase text-[#B8977E] mb-6 block transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {cfg.fullName}, LLC · Real Estate &amp; Investments
        </span>
        <h1
          className={`font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] max-w-4xl mb-6 transition-all duration-1000 delay-200 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          Real estate that builds<br/>long-term wealth.
        </h1>
        <p
          className={`font-body text-white/60 text-lg md:text-xl max-w-xl leading-relaxed mb-10 transition-all duration-1000 delay-400 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {modsPortfolio.intro.body}
        </p>
        <div
          className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-600 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <Link
            to="/mods#portfolio"
            className="inline-flex items-center justify-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 bg-white text-stone-900 hover:bg-stone-100 transition-colors duration-300 group"
            data-testid="mods-hero-portfolio-cta"
          >
            Explore Portfolio
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/mods#investors"
            className="inline-flex items-center justify-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors duration-300"
            data-testid="mods-hero-investors-cta"
          >
            Investor Partnerships
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------------- PORTFOLIO TEASER ---------------- */
const PortfolioSection = () => {
  const [ref, inView] = useInView();
  return (
    <section
      id="portfolio"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-[#FAFAF7]"
      data-testid="mods-portfolio"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className={`max-w-2xl mb-16 md:mb-20 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Portfolio
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-[1.08] whitespace-pre-line">
            {modsPortfolio.intro.headline}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200">
          {modsPortfolio.portfolioTeaser.map((item, i) => (
            <div
              key={item.name}
              className={`bg-white p-10 md:p-12 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              <span
                className={`inline-block font-body text-[10px] tracking-[0.2em] uppercase mb-6 px-3 py-1 ${
                  item.status.startsWith("Coming")
                    ? "bg-[#B8977E]/15 text-[#7C5E48]"
                    : "bg-stone-900 text-white"
                }`}
              >
                {item.status}
              </span>
              <h3 className="font-display text-3xl md:text-4xl text-stone-900 leading-tight mb-4">
                {item.name}
              </h3>
              <p className="font-body text-stone-500 text-base leading-relaxed">
                {item.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FEATURED PROPERTY (1407 7th Ave SE) ---------------- */
const FeaturedPropertySection = () => {
  const [ref, inView] = useInView();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const fp = modsPortfolio.featuredProperty;

  // Choose a curated subset for the main grid (skip the hero photo already used in section header)
  const heroPhoto = `${fp.photoBase}/10.jpg`; // front exterior
  const featuredPhotos = ["01", "05", "07", "12", "15", "18", "20", "22", "25"].map(
    (n) => `${fp.photoBase}/${n}.jpg`
  );
  const allPhotos = fp.photos.map((p) => `${fp.photoBase}/${p}`);

  return (
    <section
      id="featured"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-white"
      data-testid="mods-featured-property"
    >
      <div className="max-w-[1400px] mx-auto">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 md:mb-16 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="lg:col-span-5">
            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
              {fp.tag}
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-[1.05] mb-4">
              {fp.address}
            </h2>
            <p className="font-body text-stone-400 text-sm tracking-[0.12em] uppercase mb-8">
              {fp.city}
            </p>
            <p className="font-display text-xl md:text-2xl text-stone-700 leading-snug mb-6">
              {fp.headline}
            </p>
            <p className="font-body text-stone-500 text-base leading-relaxed">
              {fp.blurb}
            </p>
          </div>
          <div className="lg:col-span-7">
            <button
              type="button"
              onClick={() => setLightboxIdx(9)}
              className="block w-full aspect-[4/3] overflow-hidden group"
              data-testid="featured-hero-photo"
            >
              <img
                src={heroPhoto}
                alt={`${fp.address} exterior`}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              />
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {featuredPhotos.map((src, i) => {
            const allIdx = allPhotos.indexOf(src);
            return (
              <button
                key={src}
                type="button"
                onClick={() => setLightboxIdx(allIdx)}
                className={`aspect-[4/3] overflow-hidden group transition-all duration-1000 ${
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`featured-photo-${i}`}
              >
                <img
                  src={src}
                  alt={`${fp.address} photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <p className="font-body text-stone-400 text-sm">
            {fp.photos.length} photos · click any to expand
          </p>
          <button
            type="button"
            onClick={() => setLightboxIdx(0)}
            className="inline-flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase text-stone-900 group"
            data-testid="featured-view-all"
          >
            View All Photos
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <Lightbox
          photos={allPhotos}
          startIdx={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          caption={`${fp.address} · ${fp.city}`}
        />
      )}
    </section>
  );
};

/* ---------------- LIGHTBOX ---------------- */
const Lightbox = ({ photos, startIdx, onClose, caption }) => {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [photos.length, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-stone-950/97 flex flex-col"
      data-testid="featured-lightbox"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-5 text-white/70" onClick={(e) => e.stopPropagation()}>
        <span className="font-body text-[11px] tracking-[0.24em] uppercase">
          {caption} · {idx + 1} / {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 md:px-12 pb-10" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
          className="text-white/60 hover:text-white px-3 py-2 font-display text-3xl hidden md:block"
          aria-label="Previous"
        >
          ‹
        </button>
        <img
          src={photos[idx]}
          alt=""
          className="max-h-[85vh] max-w-full object-contain"
        />
        <button
          type="button"
          onClick={() => setIdx((i) => (i + 1) % photos.length)}
          className="text-white/60 hover:text-white px-3 py-2 font-display text-3xl hidden md:block"
          aria-label="Next"
        >
          ›
        </button>
      </div>
    </div>
  );
};

/* ---------------- EXTERNAL LISTINGS ---------------- */
const ExternalListingsSection = () => {
  const [ref, inView] = useInView();
  return (
    <section
      id="listings"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 bg-[#FAFAF7]"
      data-testid="mods-external-listings"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className={`max-w-2xl mb-12 md:mb-16 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Builds &amp; Listings
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-900 leading-[1.08]">
            Other homes we've built, rented, or sold.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {modsPortfolio.externalListings.map((listing, i) => (
            <a
              key={listing.address}
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`external-listing-${i}`}
              className={`group bg-white border border-stone-200 p-8 md:p-10 hover:border-stone-400 transition-all duration-500 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <span className="font-body text-[10px] tracking-[0.22em] uppercase text-stone-400">
                  {listing.note}
                </span>
                <ExternalLink
                  size={16}
                  className="text-stone-400 group-hover:text-stone-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-stone-900 leading-tight mb-2">
                {listing.address}
              </h3>
              <p className="font-body text-stone-500 text-sm mb-6">
                {listing.city}
              </p>
              <span className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-900 inline-flex items-center gap-2">
                View on {listing.source}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- TIMELINE ---------------- */
const TimelineSection = () => {
  const [ref, inView] = useInView();
  return (
    <section
      id="projects"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-white"
      data-testid="mods-projects"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className={`max-w-2xl mb-16 md:mb-20 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Our History
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-[1.08]">
            A track record built,<br/>year by year.
          </h2>
        </div>

        <div className="border-t border-stone-200">
          {modsPortfolio.timeline.map((t, i) => (
            <div
              key={`${t.year}-${t.title}`}
              className={`grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 border-b border-stone-200 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="col-span-12 md:col-span-3 lg:col-span-2 font-body text-[11px] tracking-[0.2em] uppercase text-stone-400 pt-1 md:pt-2">
                {t.year}
              </div>
              <div className="col-span-12 md:col-span-5 lg:col-span-4">
                <h3 className="font-display text-xl md:text-2xl text-stone-900 leading-tight">
                  {t.title}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-4 lg:col-span-6 font-body text-sm text-stone-500 leading-relaxed">
                {t.note}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- ELLIS ROW EXTERNAL CARD ---------------- */
const EllisRowSection = () => {
  const [ref, inView] = useInView();
  return (
    <section
      id="ellis-row"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-stone-950 text-white"
      data-testid="mods-ellis-row"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className={`lg:col-span-6 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Coming Fall 2026
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
            Ellis Row Townhomes
          </h2>
          <p className="font-body text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
            Our first multi-family build — seven distinctive peaked-roof townhomes with warm wood
            accents and contemporary design. Pre-leasing and pre-sales are now open.
            Visit the dedicated Ellis Row site for floor plans, unit details, and to register interest.
          </p>
          <a
            href={ellisRowSite}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 bg-white text-stone-900 hover:bg-stone-100 transition-colors duration-300 group"
            data-testid="mods-ellis-row-link"
          >
            Visit Ellis Row Site
            <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
        <div className={`lg:col-span-6 transition-all duration-1000 delay-300 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-2 gap-px bg-white/10">
            {[
              { value: "7", label: "Units" },
              { value: "4", label: "Rental" },
              { value: "3", label: "For Sale" },
              { value: "Fall", label: "2026" },
            ].map((stat) => (
              <div key={stat.label} className="bg-stone-950 p-8 md:p-10">
                <div className="font-display text-5xl md:text-6xl text-[#B8977E] leading-none mb-3">
                  {stat.value}
                </div>
                <div className="font-body text-[11px] tracking-[0.24em] uppercase text-white/50">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- INVESTORS ---------------- */
const InvestorsSection = () => {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", company: "", interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/investor-inquiry`, form);
      toast.success("Inquiry received. We'll respond within 48 hours.");
      setForm({ firstName: "", lastName: "", email: "", phone: "", company: "", interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="investors"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-[#FAFAF7]"
      data-testid="mods-investors"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className={`lg:col-span-5 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            Investors &amp; Partners
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.08] mb-8">
            {modsPortfolio.investors.headline}
          </h2>
          <p className="font-body text-stone-500 text-base md:text-lg leading-relaxed mb-10">
            {modsPortfolio.investors.body}
          </p>
          <ul className="space-y-4">
            {modsPortfolio.investors.bullets.map((b) => (
              <li key={b} className="flex items-start gap-4">
                <div className="w-5 h-5 mt-1 flex-shrink-0 flex items-center justify-center bg-[#B8977E]/10">
                  <Check size={12} className="text-[#B8977E]" />
                </div>
                <span className="font-body text-stone-700 text-base leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`lg:col-span-7 bg-white p-8 md:p-12 border border-stone-200 transition-all duration-1000 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h3 className="font-display text-2xl md:text-3xl text-stone-900 mb-2">
            Start the conversation
          </h3>
          <p className="font-body text-stone-500 text-sm mb-8">
            Tell us a bit about yourself. We respond personally within 48 hours.
          </p>
          <form onSubmit={submit} className="space-y-5" data-testid="mods-investor-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="First Name *" required value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} testid="investor-first-name" />
              <Field label="Last Name" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} testid="investor-last-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="investor-email" />
              <Field label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="investor-phone" />
            </div>
            <Field label="Company / Fund" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="investor-company" />
            <div>
              <label className="font-body text-[11px] tracking-[0.15em] uppercase text-stone-400 mb-2 block">
                Interest
              </label>
              <select
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                data-testid="investor-interest"
                className="w-full px-5 py-4 border border-stone-200 bg-white font-body text-base text-stone-800 focus:outline-none focus:border-[#B8977E] transition-colors duration-300 appearance-none"
              >
                <option value="">Select an option</option>
                <option value="equity-btr">Equity partnership — build-to-rent</option>
                <option value="jv-acquisition">Joint venture — multi-family acquisition</option>
                <option value="general">General investment conversation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="font-body text-[11px] tracking-[0.15em] uppercase text-stone-400 mb-2 block">
                Message
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Briefly — what kind of conversation are you looking for?"
                data-testid="investor-message"
                className="w-full px-5 py-4 border border-stone-200 bg-white font-body text-base text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-[#B8977E] transition-colors duration-300 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="investor-submit"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300 group disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Submit Inquiry"}
              <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------------- CONTACT ---------------- */
const ContactSection = ({ cfg }) => {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, division: "mods", interest: "rentals-general" });
      toast.success("Message sent! We'll be in touch within 24 hours.");
      setForm({ firstName: "", lastName: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="py-28 md:py-40 px-6 md:px-12 bg-white"
      data-testid="mods-contact"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className={`lg:col-span-5 transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
            General Contact
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-stone-900 leading-[1.08] mb-6">
            Questions about a property or a portfolio?
          </h2>
          <p className="font-body text-stone-500 text-base leading-relaxed mb-10">
            For tenant inquiries on Ellis Row, please use the dedicated{" "}
            <a href={ellisRowSite} target="_blank" rel="noopener noreferrer" className="underline hover:text-stone-900">
              Ellis Row site
            </a>
            . For everything else — drop us a line.
          </p>
          <div className="space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-400 w-20">Email</span>
              <a href={`mailto:${cfg.email}`} className="font-body text-stone-700 hover:text-stone-900 transition-colors">
                {cfg.email}
              </a>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-400 w-20">Phone</span>
              <a href={`tel:${cfg.phone}`} className="font-body text-stone-700 hover:text-stone-900 transition-colors">
                {cfg.phone}
              </a>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-body text-[11px] tracking-[0.2em] uppercase text-stone-400 w-20">Office</span>
              <span className="font-body text-stone-700">{cfg.address}</span>
            </div>
          </div>
        </div>

        <div className={`lg:col-span-7 transition-all duration-1000 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <form onSubmit={submit} className="space-y-5" data-testid="mods-contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="First Name *" required value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} testid="mods-contact-first-name" />
              <Field label="Last Name" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} testid="mods-contact-last-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="mods-contact-email" />
              <Field label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="mods-contact-phone" />
            </div>
            <div>
              <label className="font-body text-[11px] tracking-[0.15em] uppercase text-stone-400 mb-2 block">
                Message
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?"
                data-testid="mods-contact-message"
                className="w-full px-5 py-4 border border-stone-200 bg-white font-body text-base text-stone-800 placeholder:text-stone-300 focus:outline-none focus:border-[#B8977E] transition-colors duration-300 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="mods-contact-submit"
              className="inline-flex items-center gap-3 font-body text-sm tracking-[0.12em] uppercase px-8 py-4 bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300 group disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Message"}
              <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------------- shared field ---------------- */
const Field = ({ label, type = "text", required, value, onChange, testid }) => (
  <div>
    <label className="font-body text-[11px] tracking-[0.15em] uppercase text-stone-400 mb-2 block">
      {label}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-5 py-4 border border-stone-200 bg-white font-body text-base text-stone-800 focus:outline-none focus:border-[#B8977E] transition-colors duration-300"
    />
  </div>
);
